import React, { useState, useCallback, useRef } from 'react';
import { Plus, Save, Trash2, Copy, FileText } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiService } from '../services/api';
import { toast } from 'react-hot-toast';
import DefaultFirstPageEditor from '../components/DefaultFirstPageEditor';

// Block types
interface Block {
  id: string;
  type: string;
  settings: Record<string, any>;
  position: { x: number; y: number };
  children?: {
    leftColumn?: Block[];
    rightColumn?: Block[];
  };
}

interface Template {
  id?: number;
  name: string;
  description?: string;
  blocks: Block[];
  isDefault?: boolean;
}

// Available block types
const BLOCK_TYPES = [
  {
    type: 'header',
    name: 'Header Block',
    description: 'Company name, logo, work order title',
    icon: '📋',
    defaultSettings: { 
      showLogo: true, 
      title: 'Work Order', 
      fontSize: 'large',
      showCompanyName: true,
      showCompanyAddress: false,
      showCompanyPhone: false,
      showCompanyEmail: false
    }
  },
  {
    type: 'twoColumnContainer',
    name: '2-Column Container',
    description: 'Split layout into two columns',
    icon: '📐',
    defaultSettings: {
      columnGap: '16px',
      equalWidth: true
    }
  },
  {
    type: 'workOrderInfo',
    name: 'Work Order Info',
    description: 'WO#, date, priority, status',
    icon: '📝',
    defaultSettings: { showPriority: true, showStatus: true, showDate: true }
  },
  {
    type: 'companyInfo',
    name: 'Company Info',
    description: 'Company name, address, contact',
    icon: '🏛️',
    defaultSettings: { 
      showName: true, 
      showAddress: true, 
      showCity: true,
      showState: true,
      showZip: true,
      showPhone: true, 
      showEmail: true 
    }
  },
  {
    type: 'clientInfo',
    name: 'Client Info',
    description: 'Client name, address, contact',
    icon: '👤',
    defaultSettings: { showPhone: true, showAddress: true, showEmail: true }
  },
  {
    type: 'vendorInfo',
    name: 'Vendor Info',
    description: 'Primary vendor details',
    icon: '🏢',
    defaultSettings: { primaryOnly: true, showContact: true }
  },
  {
    type: 'allVendorsTable',
    name: 'All Vendors Table',
    description: 'Table of all assigned vendors',
    icon: '📊',
    defaultSettings: { showContact: true, showPhone: true }
  },
  {
    type: 'description',
    name: 'Description Block',
    description: 'Work order description',
    icon: '📄',
    defaultSettings: { maxHeight: '200px', showLabel: true }
  },
  {
    type: 'notes',
    name: 'Notes/Comments',
    description: 'Additional notes and comments',
    icon: '💬',
    defaultSettings: { showLabel: true, allowMultiple: true }
  },
  {
    type: 'signature',
    name: 'Signature Block',
    description: 'Authorization signatures',
    icon: '✍️',
    defaultSettings: { showClient: true, showVendor: true }
  },
  {
    type: 'footer',
    name: 'Footer Block',
    description: 'Terms, contact info',
    icon: '📌',
    defaultSettings: { 
      showTerms: true, 
      showContact: true,
      customText: '',
      showCustomText: false
    }
  }
];

// Block component
const BlockComponent: React.FC<{
  block: Block;
  isSelected: boolean;
  onSelect: () => void;
  onUpdate: (settings: Record<string, any>) => void;
  onDelete: () => void;
  onDuplicate: () => void;
  onDragStart: (blockId: string) => void;
}> = ({ block, isSelected, onSelect, onUpdate, onDelete, onDuplicate, onDragStart }) => {
  const blockType = BLOCK_TYPES.find(bt => bt.type === block.type);
  
  return (
    <div
      className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
        isSelected 
          ? 'border-accent-color bg-accent-color-50' 
          : 'border-gray-200 hover:border-gray-300'
      }`}
      onClick={onSelect}
      draggable={true}
      onDragStart={(e) => {
        console.log('Block drag start:', block.id);
        onDragStart(block.id);
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('text/plain', block.id);
      }}
      onDragEnd={() => {
        console.log('Block drag end:', block.id);
      }}
    >
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <span className="text-2xl">{blockType?.icon}</span>
          <div>
            <h3 className="font-medium">{blockType?.name}</h3>
            <p className="text-sm text-gray-600">{blockType?.description}</p>
          </div>
        </div>
        <div className="flex gap-1">
          <button
            onClick={(e) => {
              e.stopPropagation();
              onDuplicate();
            }}
            className="p-1 hover:bg-gray-200 rounded"
            title="Duplicate"
          >
            <Copy className="w-4 h-4" />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onDelete();
            }}
            className="p-1 hover:bg-red-200 rounded text-red-600"
            title="Delete"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>
      
      {/* Block preview */}
      <div className="text-sm text-gray-500 bg-gray-50 p-2 rounded">
        {block.type === 'header' && 'Company Header with Logo'}
        {block.type === 'workOrderInfo' && 'Work Order #12345 - High Priority'}
        {block.type === 'companyInfo' && 'Company: Your Company - 123 Main St'}
        {block.type === 'clientInfo' && 'Client: ABC Company - 123 Main St'}
        {block.type === 'vendorInfo' && 'Vendor: XYZ Services - John Doe'}
        {block.type === 'allVendorsTable' && 'Vendor Table with Contact Info'}
        {block.type === 'description' && 'Work order description text...'}
        {block.type === 'notes' && 'Additional notes and comments...'}
        {block.type === 'signature' && 'Client Signature: ___________'}
        {block.type === 'footer' && 'Terms and Contact Information'}
      </div>
    </div>
  );
};

// Container Block Component - for 2-column layouts
const ContainerBlockComponent: React.FC<{
  block: Block;
  isSelected: boolean;
  onSelect: () => void;
  onUpdate: (settings: Record<string, any>) => void;
  onDelete: () => void;
  onDuplicate: () => void;
  onDragStart: (blockId: string) => void;
  onAddBlockToColumn: (blockType: string, column: 'left' | 'right') => void;
  onRemoveBlockFromColumn: (blockId: string, column: 'left' | 'right') => void;
  onReorderBlocksInColumn: (column: 'left' | 'right', blocks: Block[]) => void;
  allBlocks: Block[];
}> = ({
  block,
  isSelected,
  onSelect,
  onDelete,
  onDuplicate,
  onAddBlockToColumn,
  onRemoveBlockFromColumn
}) => {
  const blockType = BLOCK_TYPES.find(bt => bt.type === block.type);
  const [draggedOverColumn, setDraggedOverColumn] = useState<'left' | 'right' | null>(null);

  const leftBlocks = block.children?.leftColumn || [];
  const rightBlocks = block.children?.rightColumn || [];

  const handleColumnDrop = (e: React.DragEvent, column: 'left' | 'right') => {
    e.preventDefault();
    e.stopPropagation();

    console.log('Container column drop:', { column });
    
    const blockType = e.dataTransfer.getData('text/plain');
    console.log('Block type from dataTransfer:', blockType);
    
    if (blockType && blockType !== 'twoColumnContainer') {
      onAddBlockToColumn(blockType, column);
    } else if (blockType === 'twoColumnContainer') {
      console.log('Cannot add container block into another container');
    }
    
    setDraggedOverColumn(null);
  };

  const renderNestedBlock = (nestedBlock: Block, column: 'left' | 'right') => {
    const nestedBlockType = BLOCK_TYPES.find(bt => bt.type === nestedBlock.type);

    return (
      <div
        key={nestedBlock.id}
        className="p-2 border border-gray-300 rounded bg-white dark:bg-gray-700 mb-2"
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="text-lg">{nestedBlockType?.icon}</span>
            <span className="text-sm font-medium">{nestedBlockType?.name}</span>
          </div>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onRemoveBlockFromColumn(nestedBlock.id, column);
            }}
            className="p-1 hover:bg-red-200 rounded text-red-600"
            title="Remove from container"
          >
            <Trash2 className="w-3 h-3" />
          </button>
        </div>
      </div>
    );
  };

  return (
    <div
      className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
        isSelected
          ? 'border-accent-color bg-accent-color-50'
          : 'border-gray-200 hover:border-gray-300'
      }`}
      onClick={onSelect}
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <span className="text-2xl">{blockType?.icon}</span>
          <div>
            <h3 className="font-medium">{blockType?.name}</h3>
            <p className="text-sm text-gray-600">{blockType?.description}</p>
          </div>
        </div>
        <div className="flex gap-1">
          <button
            onClick={(e) => {
              e.stopPropagation();
              onDuplicate();
            }}
            className="p-1 hover:bg-gray-200 rounded"
            title="Duplicate"
          >
            <Copy className="w-4 h-4" />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onDelete();
            }}
            className="p-1 hover:bg-red-200 rounded text-red-600"
            title="Delete"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Two column layout */}
      <div className="grid grid-cols-2 gap-4">
        {/* Left Column */}
        <div
          className={`border-2 border-dashed rounded-lg p-3 min-h-[100px] transition-colors ${
            draggedOverColumn === 'left'
              ? 'border-accent-color bg-accent-color-100'
              : 'border-gray-300 bg-gray-50 dark:bg-gray-800'
          }`}
          onDragOver={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setDraggedOverColumn('left');
          }}
          onDragLeave={(e) => {
            e.preventDefault();
            setDraggedOverColumn(null);
          }}
          onDrop={(e) => handleColumnDrop(e, 'left')}
        >
          <div className="text-xs font-medium text-gray-600 dark:text-gray-300 mb-2">
            Left Column ({leftBlocks.length} blocks)
          </div>
          {leftBlocks.length === 0 ? (
            <div className="text-xs text-gray-500 text-center py-4">
              Drop blocks here
            </div>
          ) : (
            leftBlocks.map(nestedBlock => renderNestedBlock(nestedBlock, 'left'))
          )}
        </div>

        {/* Right Column */}
        <div
          className={`border-2 border-dashed rounded-lg p-3 min-h-[100px] transition-colors ${
            draggedOverColumn === 'right'
              ? 'border-accent-color bg-accent-color-100'
              : 'border-gray-300 bg-gray-50 dark:bg-gray-800'
          }`}
          onDragOver={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setDraggedOverColumn('right');
          }}
          onDragLeave={(e) => {
            e.preventDefault();
            setDraggedOverColumn(null);
          }}
          onDrop={(e) => handleColumnDrop(e, 'right')}
        >
          <div className="text-xs font-medium text-gray-600 dark:text-gray-300 mb-2">
            Right Column ({rightBlocks.length} blocks)
          </div>
          {rightBlocks.length === 0 ? (
            <div className="text-xs text-gray-500 text-center py-4">
              Drop blocks here
            </div>
          ) : (
            rightBlocks.map(nestedBlock => renderNestedBlock(nestedBlock, 'right'))
          )}
        </div>
      </div>
    </div>
  );
};

// Block properties panel
const BlockPropertiesPanel: React.FC<{
  selectedBlock: Block | null;
  onUpdateBlock: (blockId: string, settings: Record<string, any>) => void;
}> = ({ selectedBlock, onUpdateBlock }) => {
  if (!selectedBlock) {
    return (
      <div className="p-4">
        <p className="text-sm text-gray-500 dark:text-gray-400">Select a block to edit its properties</p>
      </div>
    );
  }

  const blockType = BLOCK_TYPES.find(bt => bt.type === selectedBlock.type);

  const renderPropertyInput = (key: string, value: any) => {
    // Skip company name and address fields - these come from system settings
    // But allow show/hide toggles for phone and email
    if (key === 'companyName' || key === 'companyAddress') {
      return null;
    }
    
    if (typeof value === 'boolean') {
      return (
        <label key={key} className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={value}
            onChange={(e) => {
              const newSettings = { ...selectedBlock.settings, [key]: e.target.checked };
              onUpdateBlock(selectedBlock.id, newSettings);
            }}
            className="rounded"
          />
          <span className="text-sm capitalize text-gray-900 dark:text-gray-100">{key.replace(/([A-Z])/g, ' $1').trim()}</span>
        </label>
      );
    }
    
    // Special handling for fontSize dropdown
    if (key === 'fontSize') {
      const fontSizeOptions = [
        { value: 'small', label: 'Small' },
        { value: 'medium', label: 'Medium' },
        { value: 'large', label: 'Large' },
        { value: 'xlarge', label: 'Extra Large' },
        { value: 'xxlarge', label: 'XX Large' }
      ];
      
      return (
        <div key={key} className="space-y-1">
          <label className="text-sm font-medium capitalize text-gray-900 dark:text-gray-100">
            {key.replace(/([A-Z])/g, ' $1').trim()}
          </label>
          <select
            value={value}
            onChange={(e) => {
              const newSettings = { ...selectedBlock.settings, [key]: e.target.value };
              onUpdateBlock(selectedBlock.id, newSettings);
            }}
            className="w-full px-3 py-1 border border-gray-300 dark:border-gray-600 rounded text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
          >
            {fontSizeOptions.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
      );
    }
    
    if (typeof value === 'string') {
      return (
        <div key={key} className="space-y-1">
          <label className="text-sm font-medium capitalize text-gray-900 dark:text-gray-100">
            {key.replace(/([A-Z])/g, ' $1').trim()}
          </label>
          <input
            type="text"
            value={value}
            onChange={(e) => {
              const newSettings = { ...selectedBlock.settings, [key]: e.target.value };
              onUpdateBlock(selectedBlock.id, newSettings);
            }}
            className="w-full px-3 py-1 border border-gray-300 dark:border-gray-600 rounded text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
          />
        </div>
      );
    }
    
    return null;
  };

  return (
    <div className="p-4 bg-white dark:bg-gray-800">
      <div className="space-y-4">
        <div>
          <h4 className="font-medium text-sm mb-2 text-gray-900 dark:text-gray-100">{blockType?.name}</h4>
          <p className="text-xs text-gray-600 dark:text-gray-300">{blockType?.description}</p>
        </div>
        
        {selectedBlock.type === 'header' && (
          <div className="bg-accent-color-50 dark:bg-accent-color-900/20 p-3 rounded-md">
            <p className="text-xs text-accent-color-700 dark:text-accent-color-300">
              <strong>Company Information:</strong> Company name and address are automatically pulled from your system admin settings. Use the checkboxes above to show/hide them.
            </p>
          </div>
        )}
        
        <div className="space-y-3">
          {Object.entries(selectedBlock.settings).map(([key, value]) => 
            renderPropertyInput(key, value)
          )}
        </div>
      </div>
    </div>
  );
};

// Main template builder component
const TemplateBuilder: React.FC<{
  template: Template;
  onUpdateTemplate: (template: Template) => void;
}> = ({ template, onUpdateTemplate }) => {
  const [selectedBlockId, setSelectedBlockId] = useState<string | null>(null);
  const [draggedBlockType, setDraggedBlockType] = useState<string | null>(null);

  const selectedBlock = template.blocks.find(b => b.id === selectedBlockId);

  const addBlockToContainer = useCallback((containerId: string, blockType: string, column: 'left' | 'right') => {
    const blockTypeConfig = BLOCK_TYPES.find(bt => bt.type === blockType);
    if (!blockTypeConfig) return;

    const newBlock: Block = {
      id: `block_${Date.now()}`,
      type: blockType,
      settings: { ...blockTypeConfig.defaultSettings },
      position: { x: 0, y: 0 }
    };

    onUpdateTemplate({
      ...template,
      blocks: template.blocks.map(block => {
        if (block.id === containerId && block.type === 'twoColumnContainer') {
          return {
            ...block,
            children: {
              ...block.children,
              [column === 'left' ? 'leftColumn' : 'rightColumn']: [
                ...(column === 'left' ? (block.children?.leftColumn || []) : (block.children?.rightColumn || [])),
                newBlock
              ]
            }
          };
        }
        return block;
      })
    });
  }, [template, onUpdateTemplate]);

  const removeBlockFromContainer = useCallback((containerId: string, blockId: string, column: 'left' | 'right') => {
    onUpdateTemplate({
      ...template,
      blocks: template.blocks.map(block => {
        if (block.id === containerId && block.type === 'twoColumnContainer') {
          const columnKey = column === 'left' ? 'leftColumn' : 'rightColumn';
          return {
            ...block,
            children: {
              ...block.children,
              [columnKey]: (block.children?.[columnKey] || []).filter(b => b.id !== blockId)
            }
          };
        }
        return block;
      })
    });
  }, [template, onUpdateTemplate]);

  const addBlock = useCallback((blockType: string) => {
    const blockTypeConfig = BLOCK_TYPES.find(bt => bt.type === blockType);
    if (!blockTypeConfig) return;

    const newBlock: Block = {
      id: `block_${Date.now()}`,
      type: blockType,
      settings: { ...blockTypeConfig.defaultSettings },
      position: { x: 0, y: template.blocks.length * 200 }
    };

    onUpdateTemplate({
      ...template,
      blocks: [...template.blocks, newBlock]
    });
  }, [template, onUpdateTemplate]);

  const updateBlock = useCallback((blockId: string, settings: Record<string, any>) => {
    onUpdateTemplate({
      ...template,
      blocks: template.blocks.map(block =>
        block.id === blockId ? { ...block, settings } : block
      )
    });
  }, [template, onUpdateTemplate]);

  const deleteBlock = useCallback((blockId: string) => {
    onUpdateTemplate({
      ...template,
      blocks: template.blocks.filter(block => block.id !== blockId)
    });
    if (selectedBlockId === blockId) {
      setSelectedBlockId(null);
    }
  }, [template, onUpdateTemplate, selectedBlockId]);

  const duplicateBlock = useCallback((blockId: string) => {
    const block = template.blocks.find(b => b.id === blockId);
    if (!block) return;

    const newBlock: Block = {
      ...block,
      id: `block_${Date.now()}`,
      position: { x: block.position.x + 20, y: block.position.y + 20 }
    };

    onUpdateTemplate({
      ...template,
      blocks: [...template.blocks, newBlock]
    });
  }, [template, onUpdateTemplate]);

  const [draggedBlockId, setDraggedBlockId] = useState<string | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);

  const handleDragStart = (blockType: string) => {
    console.log('Starting drag of block type:', blockType);
    setDraggedBlockType(blockType);
  };

  const handleDragEnd = () => {
    console.log('Drag ended');
    setDraggedBlockId(null);
    setDraggedBlockType(null);
    setDragOverIndex(null);
  };

  const handleBlockDragStart = (blockId: string) => {
    setDraggedBlockId(blockId);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  const handleBlockDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    e.stopPropagation();
    console.log('Drag over index:', index);
    setDragOverIndex(index);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Only handle drops if we're not over a specific drop zone
    if (dragOverIndex === null && draggedBlockType) {
      console.log('Canvas drop - adding to end');
      addBlock(draggedBlockType);
      setDraggedBlockType(null);
    }
    setDragOverIndex(null);
  };

  const handleBlockDrop = (e: React.DragEvent, targetIndex: number) => {
    e.preventDefault();
    e.stopPropagation();
    
    console.log('Block drop:', { draggedBlockId, draggedBlockType, targetIndex });
    
    if (draggedBlockId) {
      // Reorder existing block
      const draggedBlock = template.blocks.find(block => block.id === draggedBlockId);
      if (draggedBlock) {
        const currentIndex = template.blocks.findIndex(block => block.id === draggedBlockId);
        console.log('Reordering block:', { currentIndex, targetIndex });
        
        if (currentIndex !== targetIndex) {
          const newBlocks = [...template.blocks];
          const [removed] = newBlocks.splice(currentIndex, 1);
          newBlocks.splice(targetIndex, 0, removed);
          
          onUpdateTemplate({
            ...template,
            blocks: newBlocks
          });
        }
      }
    } else if (draggedBlockType) {
      // Add new block at specific position
      const blockTypeConfig = BLOCK_TYPES.find(bt => bt.type === draggedBlockType);
      if (blockTypeConfig) {
        const newBlock: Block = {
          id: `block_${Date.now()}`,
          type: draggedBlockType,
          settings: { ...blockTypeConfig.defaultSettings },
          position: { x: 0, y: targetIndex * 200 }
        };

        const newBlocks = [...template.blocks];
        newBlocks.splice(targetIndex, 0, newBlock);
        
        onUpdateTemplate({
          ...template,
          blocks: newBlocks
        });
      }
    }
    
    setDraggedBlockId(null);
    setDraggedBlockType(null);
    setDragOverIndex(null);
  };

  return (
    <div className="flex h-full">
      {/* Left Panel - Available Blocks */}
      <div className="w-64 bg-white dark:bg-gray-900 border-r border-gray-200 dark:border-gray-700 flex flex-col">
        <div className="p-4 border-b border-gray-200">
          <h3 className="font-medium">Available Blocks</h3>
        </div>
        <div className="flex-1 overflow-y-auto p-4">
          <div className="space-y-2">
            {BLOCK_TYPES.map((blockType) => (
              <div
                key={blockType.type}
                draggable
                onDragStart={(e) => {
                  handleDragStart(blockType.type);
                  e.dataTransfer.effectAllowed = 'move';
                  e.dataTransfer.setData('text/plain', blockType.type);
                }}
                className="p-3 border border-gray-200 rounded-lg cursor-move hover:border-gray-300 hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-center gap-2">
                  <span className="text-xl">{blockType.icon}</span>
                  <div>
                    <div className="font-medium text-sm">{blockType.name}</div>
                    <div className="text-xs text-gray-500">{blockType.description}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Center - Template Canvas */}
      <div className="flex-1 flex flex-col p-4">
        <div className="bg-white dark:bg-gray-800 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg flex-1 flex flex-col p-4 min-h-0">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-medium">Template Canvas</h3>
            <div className="text-sm text-gray-500">
              Drag blocks from the left panel or click to add
            </div>
          </div>
          
          <div
            className="flex-1 overflow-y-auto space-y-2 min-h-0"
            onDragOver={handleDragOver}
            onDrop={handleDrop}
            onDragEnd={handleDragEnd}
          >
            {template.blocks.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <div className="text-4xl mb-2">📋</div>
                <p>Drag blocks here to build your template</p>
                <p className="text-sm">or click the + button to add blocks</p>
              </div>
            ) : (
              template.blocks.map((block, index) => (
                <React.Fragment key={block.id}>
                  {/* Drop zone above each block */}
                  <div
                    className={`h-4 transition-colors border-2 border-dashed rounded ${
                      dragOverIndex === index 
                        ? 'bg-accent-color-200 border-accent-color-400' 
                        : 'bg-transparent border-transparent hover:border-gray-300'
                    }`}
                    onDragOver={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleBlockDragOver(e, index);
                    }}
                    onDrop={(e) => handleBlockDrop(e, index)}
                    onDragEnd={handleDragEnd}
                  />
                  
                  {/* Render container or regular block */}
                  {block.type === 'twoColumnContainer' ? (
                    <ContainerBlockComponent
                      block={block}
                      isSelected={selectedBlockId === block.id}
                      onSelect={() => setSelectedBlockId(block.id)}
                      onUpdate={(settings) => updateBlock(block.id, settings)}
                      onDelete={() => deleteBlock(block.id)}
                      onDuplicate={() => duplicateBlock(block.id)}
                      onDragStart={handleBlockDragStart}
                      onAddBlockToColumn={(blockType, column) => addBlockToContainer(block.id, blockType, column)}
                      onRemoveBlockFromColumn={(blockId, column) => removeBlockFromContainer(block.id, blockId, column)}
                      onReorderBlocksInColumn={() => {
                        // TODO: Implement if needed
                      }}
                      allBlocks={template.blocks}
                    />
                  ) : (
                    <BlockComponent
                      block={block}
                      isSelected={selectedBlockId === block.id}
                      onSelect={() => setSelectedBlockId(block.id)}
                      onUpdate={(settings) => updateBlock(block.id, settings)}
                      onDelete={() => deleteBlock(block.id)}
                      onDuplicate={() => duplicateBlock(block.id)}
                      onDragStart={handleBlockDragStart}
                    />
                  )}
                </React.Fragment>
              ))
            )}
            
            {/* Drop zone at the end */}
            {template.blocks.length > 0 && (
              <div
                className={`h-4 transition-colors border-2 border-dashed rounded ${
                  dragOverIndex === template.blocks.length 
                    ? 'bg-accent-color-200 border-accent-color-400' 
                    : 'bg-transparent border-transparent hover:border-gray-300'
                }`}
                onDragOver={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleBlockDragOver(e, template.blocks.length);
                }}
                onDrop={(e) => handleBlockDrop(e, template.blocks.length)}
                onDragEnd={handleDragEnd}
              />
            )}
          </div>
        </div>
      </div>

      {/* Right Panel - Block Properties */}
      <div className="w-80 bg-gray-50 dark:bg-gray-800 border-l border-gray-200 dark:border-gray-700 flex flex-col">
        <div className="p-4 border-b border-gray-200 dark:border-gray-700">
          <h3 className="font-medium text-gray-900 dark:text-gray-100">Block Properties</h3>
        </div>
        <div className="flex-1 overflow-y-auto">
          <BlockPropertiesPanel
            selectedBlock={selectedBlock || null}
            onUpdateBlock={updateBlock}
          />
        </div>
      </div>
    </div>
  );
};

// Main page component
const WorkOrderTemplates2Page: React.FC = () => {
  const [selectedTemplate, setSelectedTemplate] = useState<Template | null>(null);
  const navigate = useNavigate();
  const [isCreating, setIsCreating] = useState(false);
  const [templateName, setTemplateName] = useState('');
  const [templateNameError, setTemplateNameError] = useState('');
  const [templateDescription, setTemplateDescription] = useState('');
  
  // Default first page state
  const [showDefaultFirstPage, setShowDefaultFirstPage] = useState(false);
  const [defaultFirstPageName, setDefaultFirstPageName] = useState('');
  const [defaultFirstPageNameError, setDefaultFirstPageNameError] = useState('');
  const [defaultFirstPageContent, setDefaultFirstPageContent] = useState('');
  const [defaultFirstPageActive, setDefaultFirstPageActive] = useState(false);
  const defaultFirstPageEditorRef = useRef<WYSIWYGEditorRef>(null);

  const queryClient = useQueryClient();

  // Load templates
  const { data: templatesData, isLoading } = useQuery({
    queryKey: ['workOrderTemplates'],
    queryFn: async () => {
      const response = await apiService.getWorkOrderTemplates();
      return response.data;
    },
    initialData: []
  });

  // Load default first page
  const { data: defaultFirstPageData } = useQuery({
    queryKey: ['defaultFirstPage'],
    queryFn: async () => {
      const response = await apiService.getDefaultFirstPage();
      return response.data;
    }
  });

  // Load company data for logo
  const { data: companyData } = useQuery({
    queryKey: ['company'],
    queryFn: async () => {
      const response = await apiService.getCompany();
      return response.data;
    }
  });

  // Get full logo URL for preview
  const getFullLogoUrl = (logoPath: string | null | undefined): string => {
    if (!logoPath) return '';
    
    // If it's already a full URL, return as-is
    if (logoPath.startsWith('http://') || logoPath.startsWith('https://')) {
      return logoPath;
    }
    
    // Construct the full URL using the backend's base URL
    const backendBaseUrl = process.env.REACT_APP_API_URL?.replace('/api', '') || `${window.location.protocol}//${window.location.hostname}:5269`;
    return `${backendBaseUrl}/uploads/${logoPath}`;
  };

  // Save template mutation
  const saveTemplateMutation = useMutation({
    mutationFn: async (templateData: any) => {
      console.log('Saving template data:', templateData);
      if (templateData.id) {
        return apiService.updateWorkOrderTemplate(templateData.id, templateData);
      } else {
        return apiService.createWorkOrderTemplate(templateData);
      }
    },
    onSuccess: (response) => {
      console.log('Save successful:', response);
      queryClient.invalidateQueries({ queryKey: ['workOrderTemplates'] });
      toast.success('Template saved successfully');
      setIsCreating(false);
      setSelectedTemplate(null);
    },
    onError: (error: any) => {
      console.error('Save template error:', error);
      toast.error(`Failed to save template: ${error.response?.data?.message || error.message}`);
    }
  });

  // Save default first page mutation
  const saveDefaultFirstPageMutation = useMutation({
    mutationFn: async (data: any) => {
      return apiService.updateDefaultFirstPage(data);
    },
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ['defaultFirstPage'] });
      toast.success('Default first page saved successfully');
      setShowDefaultFirstPage(false);
    },
    onError: (error: any) => {
      console.error('Save default first page error:', error);
      toast.error(`Failed to save default first page: ${error.response?.data?.message || error.message}`);
    }
  });

  const handleCreateTemplate = () => {
    // Validate template name
    if (!templateName || !templateName.trim()) {
      setTemplateNameError('Template name is required');
      return;
    }
    
    setTemplateNameError('');
    console.log('Creating template with:', { templateName, templateDescription });
    const newTemplate: Template = {
      name: templateName.trim(),
      description: templateDescription,
      blocks: []
    };
    console.log('New template:', newTemplate);
    setSelectedTemplate(newTemplate);
    setIsCreating(false);
  };

  const handleSaveTemplate = () => {
    if (!selectedTemplate) return;
    
    // Convert blocks to HTML content for the API
    const htmlContent = generateHtmlFromBlocks(selectedTemplate.blocks);
    
    // Also save the blocks structure as JSON in the HTML content for now
    // This is a temporary solution until we have a proper blocks field in the API
    const blocksJson = JSON.stringify(selectedTemplate.blocks);
    const htmlWithBlocks = `${htmlContent}\n<!--BLOCKS:${blocksJson}-->`;
    
    const templateToSave = {
      id: selectedTemplate.id, // Preserve the ID for updates
      name: templateName || selectedTemplate.name,
      htmlContent: htmlWithBlocks,
      isActive: true,
      isDefault: false
    };
    
    console.log('Saving template:', templateToSave);
    saveTemplateMutation.mutate(templateToSave);
  };

  // Replace placeholders with sample data for preview
  const replacePlaceholders = (content: string): string => {
    const placeholderMap: Record<string, string> = {
      '{{CompanyName}}': companyData?.name || 'Sample Company Name',
      '{{CompanyAddress}}': companyData?.address || '123 Sample Street',
      '{{CompanyCity}}': companyData?.city || 'Sample City',
      '{{CompanyState}}': companyData?.state || 'OH',
      '{{CompanyZip}}': companyData?.zip || '12345',
      '{{CompanyPhone}}': companyData?.phone || '(555) 123-4567',
      '{{CompanyEmail}}': companyData?.email || 'info@samplecompany.com',
      '{{CompanyWebsite}}': companyData?.website || 'www.samplecompany.com',
      '{{CompanyLogo}}': getFullLogoUrl(companyData?.logoUrl) || '{{CompanyLogo}}',
      '{{WorkOrderNumber}}': 'WO-2024-001',
      '{{WorkOrderPriority}}': 'High',
      '{{WorkOrderStatus}}': 'Open',
      '{{WorkOrderCategory}}': 'Maintenance',
      '{{WorkOrderDescription}}': 'Sample work order description',
      '{{WorkOrderCreatedDate}}': '01/15/2024',
      '{{WorkOrderDueDate}}': '01/20/2024',
      '{{WorkOrderNTE}}': '$500.00',
      '{{ClientName}}': 'Sample Client Inc.',
      '{{ClientAddress}}': '456 Client Avenue',
      '{{ClientCity}}': 'Client City',
      '{{ClientState}}': 'CA',
      '{{ClientZip}}': '90210',
      '{{ClientPhone}}': '(555) 987-6543',
      '{{ClientEmail}}': 'contact@client.com',
      '{{AssignedUserName}}': 'John Smith',
      '{{AssignedUserEmail}}': 'john.smith@company.com',
      '{{AssignedUserPhone}}': '(555) 456-7890'
    };

    let replacedContent = content;
    Object.entries(placeholderMap).forEach(([placeholder, value]) => {
      replacedContent = replacedContent.replace(new RegExp(placeholder.replace(/[{}]/g, '\\$&'), 'g'), value);
    });
    return replacedContent;
  };

  // Convert blocks JSON to HTML for preview
  const convertBlocksToHtml = (blocksJson: string): string => {
    try {
      const blocks = JSON.parse(blocksJson);
      if (!Array.isArray(blocks)) return '';
      
      return blocks.map((block: any) => {
        const { type, properties, content } = block;
        
        switch (type) {
        case 'text':
          // For text blocks, preserve HTML formatting if it exists, otherwise treat as plain text
          const textContent = content.includes('<') ? content : content.replace(/\n/g, '<br>');
          const replacedTextContent = replacePlaceholders(textContent);
          return `<div style="font-size: ${properties.fontSize === 'small' ? '14px' : properties.fontSize === 'medium' ? '16px' : properties.fontSize === 'large' ? '18px' : '20px'}; font-weight: ${properties.fontWeight}; text-align: ${properties.textAlign}; color: ${properties.color};">${replacedTextContent}</div>`;
          
          case 'logo':
            const logoUrl = getFullLogoUrl(companyData?.logoUrl) || '{{CompanyLogo}}';
            return `<div style="text-align: center; margin: 20px 0;"><img src="${logoUrl}" alt="Company Logo" style="width: ${properties.width}px; height: ${properties.height}px; max-width: none; max-height: none;" /></div>`;
          
          case 'logoAddress':
            const replacedLogoContent = replacePlaceholders(content);
            return `<div style="display: flex; align-items: flex-start; gap: 16px; margin: 20px 0;">
              <div style="width: ${properties.logoWidth}px; height: ${properties.logoHeight}px; background-image: url('${getFullLogoUrl(companyData?.logoUrl) || '{{CompanyLogo}}'}'); background-size: contain; background-repeat: no-repeat; background-position: center; flex-shrink: 0;"></div>
              <div style="flex: 1; font-size: ${properties.fontSize === 'small' ? '14px' : properties.fontSize === 'medium' ? '16px' : properties.fontSize === 'large' ? '18px' : '20px'}; font-weight: ${properties.fontWeight}; line-height: 1.4;">
                <pre style="white-space: pre-wrap; font-family: inherit; margin: 0;">${replacedLogoContent}</pre>
              </div>
            </div>`;
          
          case 'companyName':
            const replacedCompanyName = replacePlaceholders(content);
            return `<h1 style="font-size: ${properties.fontSize === 'small' ? '18px' : properties.fontSize === 'medium' ? '24px' : properties.fontSize === 'large' ? '32px' : '40px'}; font-weight: ${properties.fontWeight}; text-align: ${properties.textAlign}; margin: 20px 0;">${replacedCompanyName}</h1>`;
          
          case 'companyAddress':
          case 'companyPhone':
          case 'companyEmail':
          case 'companyWebsite':
            const replacedOtherContent = replacePlaceholders(content);
            return `<p style="font-size: ${properties.fontSize === 'small' ? '14px' : properties.fontSize === 'medium' ? '16px' : properties.fontSize === 'large' ? '18px' : '20px'}; font-weight: ${properties.fontWeight}; text-align: ${properties.textAlign}; margin: 10px 0;">
              <pre style="white-space: pre-wrap; font-family: inherit; margin: 0;">${replacedOtherContent}</pre>
            </p>`;
          
          default:
            return `<p>${content}</p>`;
        }
      }).join('');
    } catch (error) {
      console.error('Failed to convert blocks to HTML:', error);
      return '';
    }
  };

  const handleSaveDefaultFirstPage = () => {
    // Validate template name
    if (!defaultFirstPageName || !defaultFirstPageName.trim()) {
      setDefaultFirstPageNameError('Template name is required');
      return;
    }
    
    setDefaultFirstPageNameError('');
    const isValidJson = (() => {
      try { JSON.parse(defaultFirstPageContent); return true; } catch { return false; }
    })();
    const htmlContent = isValidJson ? convertBlocksToHtml(defaultFirstPageContent) : defaultFirstPageContent;
    const htmlWithJson = isValidJson ? `<!-- JSON_DATA:${defaultFirstPageContent} -->\n${htmlContent}` : htmlContent;
    const dataToSave = {
      name: defaultFirstPageName.trim(),
      htmlContent: htmlWithJson,
      isActive: defaultFirstPageActive
    };
    saveDefaultFirstPageMutation.mutate(dataToSave);
  };

  const handleLoadDefaultFirstPage = () => {
    if (defaultFirstPageData) {
      setDefaultFirstPageName(defaultFirstPageData.name || '');
      // Extract JSON from HTML comment if it exists (allow multiline)
      const htmlContent = defaultFirstPageData.htmlContent || '';
      const jsonMatch = htmlContent.match(/<!-- JSON_DATA:([\s\S]*?) -->/);
      const jsonContent = jsonMatch ? jsonMatch[1] : '';
      setDefaultFirstPageContent(jsonContent || htmlContent);
      setDefaultFirstPageActive(defaultFirstPageData.isActive || false);
    }
    setDefaultFirstPageNameError('');
    setShowDefaultFirstPage(true);
  };

  const insertCompanyElement = (elementType: string) => {
    if (!defaultFirstPageEditorRef.current) return;
    
    switch (elementType) {
      case 'logo':
        // Insert company logo with preview
        defaultFirstPageEditorRef.current.insertCompanyLogo();
        break;
      case 'name':
        // Insert heading with company name
        defaultFirstPageEditorRef.current.insertHeading('{{CompanyName}}', 1);
        break;
      case 'address':
        // Insert paragraph with address
        defaultFirstPageEditorRef.current.insertParagraph('{{CompanyAddress}}\n{{CompanyCity}}, {{CompanyState}} {{CompanyZip}}');
        break;
      case 'phone':
        // Insert paragraph with phone
        defaultFirstPageEditorRef.current.insertParagraph('📞 {{CompanyPhone}}');
        break;
      case 'email':
        // Insert paragraph with email
        defaultFirstPageEditorRef.current.insertParagraph('✉️ {{CompanyEmail}}');
        break;
      case 'website':
        // Insert paragraph with website
        defaultFirstPageEditorRef.current.insertParagraph('🌐 {{CompanyWebsite}}');
        break;
    }
  };

  // Convert blocks to HTML content
  const generateHtmlFromBlocks = (blocks: Block[]): string => {
    if (blocks.length === 0) {
      return '<p>Empty template</p>';
    }
    
    const htmlParts = blocks.map(block => {
      switch (block.type) {
        case 'header':
          return `<div class="template-header"><h1>${block.settings.title || 'Work Order'}</h1></div>`;
        case 'workOrderInfo':
          return `<div class="template-workorder-info"><p>Work Order Information</p></div>`;
        case 'clientInfo':
          return `<div class="template-client-info"><p>Client Information</p></div>`;
        case 'vendorInfo':
          return `<div class="template-vendor-info"><p>Vendor Information</p></div>`;
        case 'allVendorsTable':
          return `<div class="template-vendors-table"><p>All Vendors Table</p></div>`;
        case 'description':
          return `<div class="template-description"><p>Work Order Description</p></div>`;
        case 'notes':
          return `<div class="template-notes"><p>Notes and Comments</p></div>`;
        case 'signature':
          return `<div class="template-signature"><p>Signature Block</p></div>`;
        case 'footer':
          return `<div class="template-footer"><p>Footer Information</p></div>`;
        default:
          return `<div class="template-block"><p>Unknown block type</p></div>`;
      }
    });
    
    return htmlParts.join('\n');
  };

  const handleCancelCreate = () => {
    setIsCreating(false);
    setSelectedTemplate(null);
    setTemplateName('');
    setTemplateDescription('');
    setTemplateNameError('');
  };

  const handleLoadTemplate = (template: any) => {
    console.log('Loading template:', template);
    
    // If template has blocks, use them directly
    if (template.blocks && Array.isArray(template.blocks) && template.blocks.length > 0) {
      console.log('Using existing blocks:', template.blocks);
      
      // Migrate existing header blocks to include phone/email settings
      const migratedBlocks = template.blocks.map((block: any) => {
        if (block.type === 'header') {
          return {
            ...block,
            settings: {
              ...block.settings,
              // Add missing phone/email settings if they don't exist
              showCompanyPhone: block.settings.showCompanyPhone ?? false,
              showCompanyEmail: block.settings.showCompanyEmail ?? false
            }
          };
        }
        return block;
      });
      
      const migratedTemplate = {
        ...template,
        blocks: migratedBlocks
      };
      
      setSelectedTemplate(migratedTemplate);
      setTemplateName(template.name);
      setTemplateDescription(template.description || '');
      setIsCreating(false);
      return;
    }
    
    // Try to extract blocks from HTML content (our temporary storage)
    let blocks: Block[] = [];
    let cleanHtmlContent = template.htmlContent || '';
    
    if (template.htmlContent && template.htmlContent.includes('<!--BLOCKS:')) {
      try {
        const blocksMatch = template.htmlContent.match(/<!--BLOCKS:(.*?)-->/s);
        if (blocksMatch && blocksMatch[1]) {
          blocks = JSON.parse(blocksMatch[1]);
          
          // Migrate existing header blocks to include phone/email settings
          blocks = blocks.map(block => {
            if (block.type === 'header') {
              return {
                ...block,
                settings: {
                  ...block.settings,
                  // Add missing phone/email settings if they don't exist
                  showCompanyPhone: block.settings.showCompanyPhone ?? false,
                  showCompanyEmail: block.settings.showCompanyEmail ?? false
                }
              };
            }
            return block;
          });
          
          // Remove the blocks comment from HTML content
          cleanHtmlContent = template.htmlContent.replace(/\n<!--BLOCKS:.*?-->/s, '').trim();
          console.log('Extracted blocks from HTML content:', blocks);
        }
      } catch (error) {
        console.error('Error parsing blocks from HTML content:', error);
        blocks = [];
      }
    }
    
    // If no blocks found, try to parse HTML content
    if (blocks.length === 0 && cleanHtmlContent) {
      blocks = parseHtmlToBlocks(cleanHtmlContent);
    }
    
    const templateWithBlocks = {
      ...template,
      blocks: blocks
    };
    
    console.log('Created template with blocks:', templateWithBlocks);
    setSelectedTemplate(templateWithBlocks);
    setTemplateName(template.name);
    setTemplateDescription(template.description || '');
    setIsCreating(false);
  };

  // Convert HTML content back to blocks (simplified version)
  const parseHtmlToBlocks = (htmlContent: string): Block[] => {
    // For now, create a simple default structure
    // In a full implementation, you'd parse the HTML and recreate blocks
    const defaultBlocks: Block[] = [
      {
        id: `block_${Date.now()}`,
        type: 'header',
        settings: { showLogo: true, title: 'Work Order', fontSize: 'large' },
        position: { x: 0, y: 0 }
      },
      {
        id: `block_${Date.now() + 1}`,
        type: 'workOrderInfo',
        settings: { showPriority: true, showStatus: true, showDate: true },
        position: { x: 0, y: 200 }
      }
    ];
    
    return defaultBlocks;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500 dark:text-gray-400">Loading templates...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <FileText className="h-8 w-8 text-gray-600 dark:text-gray-400" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Work Order Templates</h1>
            <p className="text-sm text-gray-500 dark:text-gray-400">Build custom templates with drag-and-drop blocks</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {!isCreating && !selectedTemplate && (
            <button
              onClick={() => setIsCreating(true)}
              className="flex items-center gap-2 bg-accent-color text-white px-4 py-2 rounded-lg hover:bg-accent-color-600"
            >
              <Plus className="w-4 h-4" />
              Create Template
            </button>
          )}
          {selectedTemplate && (
            <>
              <button
                onClick={handleSaveTemplate}
                disabled={saveTemplateMutation.isPending}
                className="flex items-center gap-2 bg-accent-color text-white px-4 py-2 rounded-lg hover:bg-accent-color-600 disabled:opacity-50"
              >
                <Save className="w-4 h-4" />
                {saveTemplateMutation.isPending ? 'Saving...' : 'Save'}
              </button>
              <button
                onClick={() => setSelectedTemplate(null)}
                className="btn-secondary"
              >
                Back
              </button>
            </>
          )}
        </div>
      </div>

      {isCreating ? (
        <div className="space-y-6">
          {/* Template creation form */}
          <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6">
            <h2 className="text-lg font-medium mb-4 text-gray-900 dark:text-gray-100">Create New Template</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">
                  Template Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={templateName}
                  onChange={(e) => {
                    setTemplateName(e.target.value);
                    if (templateNameError) {
                      setTemplateNameError('');
                    }
                  }}
                  placeholder="Enter template name"
                  className={`w-full px-3 py-2 border rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 ${
                    templateNameError 
                      ? 'border-red-500 dark:border-red-500' 
                      : 'border-gray-300 dark:border-gray-600'
                  }`}
                />
                {templateNameError && (
                  <p className="text-red-500 text-sm mt-1">{templateNameError}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Description</label>
                <textarea
                  value={templateDescription}
                  onChange={(e) => setTemplateDescription(e.target.value)}
                  placeholder="Enter template description"
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                />
              </div>
              <div className="flex gap-2">
                <button
                  onClick={handleCreateTemplate}
                  className="bg-accent-color text-white px-4 py-2 rounded-lg hover:bg-accent-color-600"
                >
                  Start Building
                </button>
                <button
                  onClick={handleCancelCreate}
                  className="btn-secondary"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : selectedTemplate ? (
        <div className="space-y-6">
          {/* Template builder */}
          <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg">
            <div className="p-4 border-b border-gray-200 dark:border-gray-700">
              <div className="flex-1 pr-4">
                <label className="block text-sm font-medium mb-1">Template Title</label>
                <input
                  type="text"
                  value={templateName}
                  onChange={(e) => setTemplateName(e.target.value)}
                  placeholder="Enter template title"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg mb-3 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                />
                <label className="block text-sm font-medium mb-1">Description</label>
                <input
                  type="text"
                  value={templateDescription}
                  onChange={(e) => setTemplateDescription(e.target.value)}
                  placeholder="Optional description"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                />
              </div>
              {/* Buttons are in top page header now; removed here for consistency */}
            </div>
            <div className="h-[600px]">
              <TemplateBuilder
                template={selectedTemplate}
                onUpdateTemplate={setSelectedTemplate}
              />
            </div>
          </div>
        </div>
      ) : (
        <div>
          {/* Default First Page Section - styled like template card */}
          <div className="mb-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:border-gray-300 dark:hover:border-gray-600">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-medium text-gray-900 dark:text-gray-100">Default First Page</h3>
                      {defaultFirstPageData?.isActive && (
                        <span className="px-2 py-0.5 bg-green-100 text-green-800 text-xs rounded-full">Active</span>
                      )}
                    </div>
                    <p className="text-sm text-gray-600 dark:text-gray-300 mb-2">Shown before every work order</p>
                    <div className="text-xs text-gray-500 dark:text-gray-400 space-y-1">
                      <div><span className="text-gray-700 dark:text-gray-300">Name:</span> {defaultFirstPageData?.name || 'Unnamed'}</div>
                      {defaultFirstPageData?.createdDate && (
                        <div><span className="text-gray-700 dark:text-gray-300">Created:</span> {new Date(defaultFirstPageData.createdDate).toLocaleDateString()}</div>
                      )}
                      {defaultFirstPageData?.updatedDate && (
                        <div><span className="text-gray-700 dark:text-gray-300">Updated:</span> {new Date(defaultFirstPageData.updatedDate).toLocaleDateString()}</div>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={() => navigate('/workorders/templates/first-page')}
                    className="self-start px-3 py-2 bg-accent-color text-white rounded-lg hover:bg-accent-color-600"
                  >
                    {defaultFirstPageData ? 'Edit' : 'Create'}
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Templates Section */}
          <div className="mb-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-medium text-gray-900">Work Order Templates</h3>
            </div>
          </div>

          {(templatesData || []).length === 0 ? (
            <div className="text-center py-12">
              <div className="text-4xl mb-4">📋</div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">No Templates Yet</h3>
              <p className="text-gray-600 mb-4">Create your first work order template using the block builder</p>
              <button
                onClick={() => setIsCreating(true)}
                className="bg-accent-color text-white px-6 py-2 rounded-lg hover:bg-accent-color-600"
              >
                Create Your First Template
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {(templatesData || []).map((template: any) => (
                <div
                  key={template.id}
                  className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:border-gray-300 dark:hover:border-gray-600 cursor-pointer"
                  onClick={() => handleLoadTemplate(template)}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <h3 className="font-medium mb-1">{template.name}</h3>
                      <p className="text-sm text-gray-600 mb-2">{template.description || 'No description'}</p>
                      <div className="text-xs text-gray-500 space-y-1">
                        {template.createdDate && (
                          <div><span className="text-gray-700">Created:</span> {new Date(template.createdDate).toLocaleDateString()}</div>
                        )}
                        {template.updatedDate && (
                          <div><span className="text-gray-700">Updated:</span> {new Date(template.updatedDate).toLocaleDateString()}</div>
                        )}
                        <div className="flex items-center gap-2">
                          {template.isDefault && (
                            <span className="px-2 py-0.5 bg-accent-color-100 text-accent-color-800 text-xs rounded-full">Default</span>
                          )}
                          {template.isActive ? (
                            <span className="px-2 py-0.5 bg-green-100 text-green-800 text-xs rounded-full">Active</span>
                          ) : (
                            <span className="px-2 py-0.5 bg-gray-100 text-gray-800 text-xs rounded-full">Inactive</span>
                          )}
                        </div>
                      </div>
                    </div>
                    <button
                      onClick={(e) => { e.stopPropagation(); handleLoadTemplate(template); }}
                      className="self-start px-3 py-2 bg-gray-100 text-gray-800 rounded-lg hover:bg-gray-200"
                    >
                      Edit
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Default First Page Editor - full page (no modal) */}
      {showDefaultFirstPage && (
        <div className="space-y-6">
          <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg">
            <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
              <div>
                <h2 className="text-lg font-medium text-gray-900 dark:text-gray-100">Default First Page</h2>
                <p className="text-sm text-gray-600 dark:text-gray-300">Configure the page shown before all work orders</p>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={handleSaveDefaultFirstPage}
                  disabled={saveDefaultFirstPageMutation.isPending}
                  className="flex items-center gap-2 bg-accent-color text-white px-4 py-2 rounded-lg hover:bg-accent-color-600 disabled:opacity-50"
                >
                  <Save className="w-4 h-4" />
                  {saveDefaultFirstPageMutation.isPending ? 'Saving...' : 'Save Default First Page'}
                </button>
                <button
                  onClick={() => {
                    setShowDefaultFirstPage(false);
                    setDefaultFirstPageNameError('');
                  }}
                  className="btn-secondary"
                >
                  Back
                </button>
              </div>
            </div>

            <div className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Template Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={defaultFirstPageName}
                  onChange={(e) => {
                    setDefaultFirstPageName(e.target.value);
                    if (defaultFirstPageNameError) {
                      setDefaultFirstPageNameError('');
                    }
                  }}
                  className={`w-full px-3 py-2 border rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 ${
                    defaultFirstPageNameError 
                      ? 'border-red-500 dark:border-red-500' 
                      : 'border-gray-300 dark:border-gray-600 focus:ring-2 focus:ring-accent-color focus:border-accent-color'
                  }`}
                  placeholder="Enter template name"
                />
                {defaultFirstPageNameError && (
                  <p className="text-red-500 text-sm mt-1">{defaultFirstPageNameError}</p>
                )}
              </div>

              <div className="h-[600px]">
                <DefaultFirstPageEditor
                  key={defaultFirstPageContent}
                  initialContent={defaultFirstPageContent}
                  onSave={(content) => setDefaultFirstPageContent(content)}
                  companyData={{
                    name: companyData?.name,
                    address: companyData?.address,
                    city: companyData?.city,
                    state: companyData?.state,
                    zip: companyData?.zip,
                    phone: companyData?.phone,
                    email: companyData?.email,
                    website: companyData?.website,
                    logoUrl: getFullLogoUrl(companyData?.logoUrl)
                  }}
                />
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="defaultFirstPageActive"
                  checked={defaultFirstPageActive}
                  onChange={(e) => setDefaultFirstPageActive(e.target.checked)}
                  className="rounded"
                />
                <label htmlFor="defaultFirstPageActive" className="text-sm font-medium text-gray-700">
                  Enable this default first page
                </label>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WorkOrderTemplates2Page;
